/**
 * GroupScoringView
 *
 * Mobile-first scoring surface for Group Prioritization sessions.
 * One option at a time, swipeable, with per-criterion score rows.
 *
 * Fixed header: progress bar + presence strip
 * Scrollable body: option detail + score rows
 * Fixed footer: navigation buttons
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useGroupSessionStore } from '../../stores/prioritization/groupSessionStore';
import type { GroupMemberPresence, GroupOption } from '../../types/prioritization/groupSession';
import { ReverseActionButton } from '../reversals/ReverseActionButton';
import { withdrawGroupScores } from '../../lib/reversals/reversalsService';
import { WITHDRAW_MY_SCORES_LABEL } from '../../lib/sessions/vocabulary';

// Revealing is irreversible for the room, so every reveal button asks twice —
// the scoring header and the "you're done" screen used to differ (one click vs
// two). One label pair for both.
const REVEAL_RESULTS_LABEL = 'Reveal results';
const REVEAL_CONFIRM_LABEL = 'Confirm — reveal now';

// ─── Props ────────────────────────────────────────────────────────────────────

interface GroupScoringViewProps {
  sessionId: string;
  isHost: boolean;
}

// ─── Presence Avatar ──────────────────────────────────────────────────────────

function PresenceAvatar({
  member,
}: {
  member: GroupMemberPresence;
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  const statusColor =
    member.status === 'complete'
      ? 'var(--green)'
      : member.status === 'scoring'
      ? 'var(--mustard)'
      : 'var(--slate)';

  const isPulsing = member.status === 'scoring';

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onTouchStart={() => setShowTooltip((v) => !v)}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'var(--surface)',
          border: `2px solid ${statusColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--sans)',
          fontWeight: 700,
          fontSize: '13px',
          color: statusColor,
          cursor: 'pointer',
          boxShadow: isPulsing ? `0 0 0 0 ${statusColor}` : undefined,
          animation: isPulsing ? 'pp-presence-pulse 2s ease-in-out infinite' : undefined,
          userSelect: 'none',
        }}
      >
        {(member.displayName ?? '?').charAt(0).toUpperCase()}
      </div>
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            bottom: '38px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            border: '1px solid var(--rule-bright)',
            borderRadius: '6px',
            padding: '6px 10px',
            whiteSpace: 'nowrap',
            zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '12px',
              color: 'var(--text-pri)',
            }}
          >
            {member.displayName ?? 'Participant'}
          </span>
          <br />
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '11px',
              color: 'var(--text-sec)',
            }}
          >
            {member.optionsScored} of {member.totalOptions} scored
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Score Button Row ─────────────────────────────────────────────────────────

function ScoreRow({
  criteriaId,
  itemId,
  currentScore,
  onScore,
}: {
  criteriaId: string;
  itemId: string;
  currentScore: number | undefined;
  onScore: (itemId: string, criteriaId: string, score: number) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
      }}
      role="group"
      aria-label="Score 1 to 5"
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const selected = currentScore === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onScore(itemId, criteriaId, n)}
            aria-pressed={selected}
            aria-label={`Score ${n}`}
            style={{
              minWidth: '44px',
              minHeight: '44px',
              flex: 1,
              background: selected ? 'var(--mustard)' : 'var(--bg-raised)',
              border: selected ? '2px solid var(--mustard)' : '2px solid var(--rule-bright)',
              borderRadius: '8px',
              color: selected ? '#0B0F19' : 'var(--text-sec)',
              fontFamily: 'var(--mono)',
              fontSize: '16px',
              fontWeight: selected ? 800 : 500,
              cursor: 'pointer',
              transition: 'background 0.12s, color 0.12s, border-color 0.12s, transform 0.08s',
              transform: selected ? 'scale(1.05)' : 'scale(1)',
              lineHeight: 1,
            }}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

// ─── Collapsible Note ─────────────────────────────────────────────────────────

function NoteField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(!!value);

  return (
    <div style={{ marginTop: '6px' }}>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--text-ter)',
            fontFamily: 'var(--sans)',
            fontSize: '12px',
            cursor: 'pointer',
            padding: '4px 0',
          }}
        >
          + Add note
        </button>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Optional note about this score…"
          style={{
            width: '100%',
            background: 'var(--bg-raised)',
            border: '1px solid var(--rule-bright)',
            borderRadius: '6px',
            color: 'var(--text-pri)',
            fontFamily: 'var(--sans)',
            fontSize: '13px',
            padding: '8px 10px',
            resize: 'vertical',
            minHeight: '60px',
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />
      )}
    </div>
  );
}

// ─── Completion State ─────────────────────────────────────────────────────────

function ScoringComplete({ isHost, presenceMembers, onReveal, revealConfirm, revealError, withdraw }: {
  isHost: boolean;
  /** "Withdraw my scores" (migration 768) — undo before the reveal. */
  withdraw?: React.ReactNode;
  presenceMembers: GroupMemberPresence[];
  /** The same two-click confirm as the scoring header (handleReveal). */
  onReveal: () => void;
  revealConfirm: boolean;
  revealError?: string | null;
}) {
  const complete = presenceMembers.filter((m) => m.status === 'complete').length;
  const total = presenceMembers.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        padding: '60px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '48px' }}>✓</div>
      <h2
        className="dark:!text-[var(--text-ter)]"
        style={{
          fontFamily: 'var(--serif)',
          fontSize: '22px',
          color: 'var(--green)',
          margin: 0,
        }}
      >
        You're done scoring!
      </h2>
      <p
        style={{
          fontFamily: 'var(--sans)',
          fontSize: '14px',
          color: 'var(--text-sec)',
          margin: 0,
          lineHeight: 1.6,
        }}
      >
        {complete} of {total} {total === 1 ? 'member has' : 'members have'} finished.
        {!isHost && ' The host will reveal results when everyone is ready.'}
      </p>
      {isHost && (
        <button
          type="button"
          onClick={onReveal}
          style={{
            background: revealConfirm ? 'var(--coral)' : 'var(--mustard)',
            border: 'none',
            borderRadius: '10px',
            color: '#0B0F19',
            fontFamily: 'var(--sans)',
            fontWeight: 700,
            fontSize: '15px',
            padding: '14px 32px',
            cursor: 'pointer',
          }}
        >
          {revealConfirm ? REVEAL_CONFIRM_LABEL : REVEAL_RESULTS_LABEL}
        </button>
      )}
      {isHost && revealError && <RevealErrorLine message={revealError} />}
      {withdraw}
    </div>
  );
}

function RevealErrorLine({ message }: { message: string }) {
  return (
    <p
      role="alert"
      style={{
        fontFamily: 'var(--sans)',
        fontSize: '13px',
        color: 'var(--coral)',
        margin: 0,
        lineHeight: 1.5,
      }}
    >
      {message}
    </p>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function GroupScoringView({ sessionId, isHost }: GroupScoringViewProps) {
  const {
    options,
    criteria,
    myScores: myScoresMap,
    presenceMembers,
    allScores,
    isOnline,
    isLoading,
    submitScore,
    revealResults,
    revealError,
    broadcastPresence,
    loadGroupSession,
  } = useGroupSessionStore();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [allDoneForMe, setAllDoneForMe] = useState(false);
  const [revealConfirm, setRevealConfirm] = useState(false);

  // Touch swipe tracking
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const currentOption: GroupOption | undefined = options[currentIndex];

  // Flatten myScores (Record<itemId, Record<criteriaId, GroupScore>>) into a flat lookup
  const scoreMap = useMemo(() => {
    const map: Record<string, number> = {};
    Object.entries(myScoresMap).forEach(([itemId, byCriteria]) => {
      Object.entries(byCriteria).forEach(([criteriaId, gs]) => {
        map[`${itemId}::${criteriaId}`] = gs.score;
      });
    });
    return map;
  }, [myScoresMap]);

  // Count of options where all criteria have been scored by current user
  const optionsScoredCount = useMemo(() => {
    return options.filter((opt) =>
      criteria.every((c) => scoreMap[`${opt.id}::${c.id}`] !== undefined),
    ).length;
  }, [options, criteria, scoreMap]);

  // Broadcast presence whenever index or scored count changes
  useEffect(() => {
    broadcastPresence(sessionId, {
      currentOptionIndex: currentIndex,
      optionsScored: optionsScoredCount,
      totalOptions: options.length,
      status: allDoneForMe ? 'complete' : 'scoring',
    });
  }, [sessionId, currentIndex, optionsScoredCount, allDoneForMe, options.length, broadcastPresence]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (currentIndex < options.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      // Check if all scored using flat scoreMap
      const allScored = options.every((opt) =>
        criteria.every((c) => scoreMap[`${opt.id}::${c.id}`] !== undefined),
      );
      if (allScored) setAllDoneForMe(true);
    }
  }, [currentIndex, options, criteria, scoreMap]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  }, [currentIndex]);

  // ── Touch swipe ────────────────────────────────────────────────────────────
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      // Only treat as horizontal swipe if horizontal movement dominates
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        if (dx < 0) goNext();
        else goPrev();
      }
      touchStartX.current = null;
      touchStartY.current = null;
    },
    [goNext, goPrev],
  );

  // ── Score tap ──────────────────────────────────────────────────────────────
  const handleScore = useCallback(
    (itemId: string, criteriaId: string, score: number) => {
      const note = notes[`${itemId}::${criteriaId}`];
      submitScore(itemId, criteriaId, score, note);
    },
    [submitScore, notes],
  );

  const handleNoteChange = useCallback((itemId: string, criteriaId: string, note: string) => {
    setNotes((prev) => ({ ...prev, [`${itemId}::${criteriaId}`]: note }));
    // Re-submit with note if already scored
    const existingScore = scoreMap[`${itemId}::${criteriaId}`];
    if (existingScore !== undefined) {
      submitScore(itemId, criteriaId, existingScore, note || undefined);
    }
  }, [scoreMap, submitScore]);

  // ── Host reveal ────────────────────────────────────────────────────────────
  const handleReveal = useCallback(async () => {
    if (!revealConfirm) {
      setRevealConfirm(true);
      setTimeout(() => setRevealConfirm(false), 4000);
      return;
    }
    await revealResults(sessionId);
    setRevealConfirm(false);
  }, [revealConfirm, revealResults, sessionId]);

  // ── Withdraw my scores (migration 768, owner ruling #1) ────────────────────
  // Only offered while scoring and only once this person has scored something;
  // after reveal the server refuses (a revealed result is superseded, not
  // undone) and the refusal's remedy reaches the modal.
  const hasMyScores = Object.keys(scoreMap).length > 0;
  const withdrawControl = hasMyScores ? (
    <ReverseActionButton
      label={WITHDRAW_MY_SCORES_LABEL}
      confirmTitle={WITHDRAW_MY_SCORES_LABEL}
      explain="Your scores stop counting and you can score again. They are kept in the session's history with your reason. Once the host reveals results, scores can no longer be withdrawn."
      run={(reason) => withdrawGroupScores(sessionId, reason)}
      onDone={() => {
        setAllDoneForMe(false);
        setCurrentIndex(0);
        void loadGroupSession(sessionId);
      }}
      variant="ghost"
      size="sm"
    />
  ) : null;

  // ── Host stats ─────────────────────────────────────────────────────────────
  const membersComplete = presenceMembers.filter((m) => m.status === 'complete').length;

  // ── Progress ───────────────────────────────────────────────────────────────
  const progressPct = options.length > 0 ? (optionsScoredCount / options.length) * 100 : 0;

  const isLastOption = currentIndex === options.length - 1;
  const currentOptionAllScored =
    currentOption !== undefined &&
    criteria.every((c) => scoreMap[`${currentOption.id}::${c.id}`] !== undefined);

  // ── Show completion state ──────────────────────────────────────────────────
  if (allDoneForMe) {
    return (
      <>
        <style>{`
          @keyframes pp-spin { to { transform: rotate(360deg); } }
          @keyframes pp-presence-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(212,160,23,0.5); }
            50% { box-shadow: 0 0 0 6px rgba(212,160,23,0); }
          }
        `}</style>
        <ScoringComplete
          isHost={isHost}
          presenceMembers={presenceMembers}
          onReveal={() => void handleReveal()}
          revealConfirm={revealConfirm}
          revealError={revealError}
          withdraw={withdrawControl}
        />
      </>
    );
  }

  if (options.length === 0 || !currentOption) {
    return (
      <div
        style={{
          padding: '48px 0',
          textAlign: 'center',
          fontFamily: 'var(--sans)',
          fontSize: '14px',
          color: 'var(--text-ter)',
        }}
      >
        No options to score yet.
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes pp-spin { to { transform: rotate(360deg); } }
        @keyframes pp-presence-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212,160,23,0.5); }
          50% { box-shadow: 0 0 0 6px rgba(212,160,23,0); }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 100px)',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* ── Offline banner ─────────────────────────────────────────────────── */}
        {!isOnline && (
          <div
            style={{
              background: 'var(--coral-dim)',
              border: '1px solid var(--coral)',
              borderRadius: '6px',
              padding: '8px 14px',
              marginBottom: '12px',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--coral)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>⚠</span>
            You're offline. Scores will be saved and synced when you reconnect.
          </div>
        )}

        {/* ── Fixed header area ───────────────────────────────────────────────── */}
        <div
          style={{
            marginBottom: '16px',
          }}
        >
          {/* Progress row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '12px',
                color: 'var(--text-ter)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {currentIndex + 1} of {options.length}
            </span>
            {/* Progress bar */}
            <div
              style={{
                flex: 1,
                height: '4px',
                background: 'var(--rule-bright)',
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progressPct}%`,
                  background: 'var(--mustard)',
                  borderRadius: '2px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '11px',
                color: 'var(--text-ter)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {optionsScoredCount}/{options.length} done
            </span>
          </div>

          {/* Presence strip */}
          {presenceMembers.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '10px',
                  color: 'var(--text-ter)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  marginRight: '2px',
                }}
              >
                Members
              </span>
              {presenceMembers.map((m) => (
                <PresenceAvatar key={m.userId} member={m} />
              ))}
            </div>
          )}
        </div>

        {/* ── Option card (scrollable) ─────────────────────────────────────────── */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--rule)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
            }}
          >
            <h2
              style={{
                fontFamily: 'var(--serif)',
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--text-pri)',
                margin: '0 0 8px',
                lineHeight: 1.3,
              }}
            >
              {currentOption.title}
            </h2>
            {currentOption.description && (
              <p
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '14px',
                  color: 'var(--text-sec)',
                  lineHeight: 1.6,
                  margin: '0 0 8px',
                }}
              >
                {currentOption.description}
              </p>
            )}
            {currentOption.url && (
              <a
                href={currentOption.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '12px',
                  color: 'var(--mustard)',
                  wordBreak: 'break-all',
                  display: 'block',
                }}
              >
                {currentOption.url}
              </a>
            )}
          </div>

          {/* Criteria score rows */}
          {criteria.map((criterion) => {
            const key = `${currentOption.id}::${criterion.id}`;
            const scored = scoreMap[key];
            const totalWeight = criteria.reduce((s, c) => s + c.weight, 0) || 1;
            const pct = Math.round((criterion.weight / totalWeight) * 100);

            return (
              <div
                key={criterion.id}
                style={{
                  background: 'var(--surface)',
                  border: scored !== undefined ? '1px solid var(--rule-bright)' : '1px solid var(--rule)',
                  borderRadius: '10px',
                  padding: '16px',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '10px',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--sans)',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'var(--text-pri)',
                      flex: 1,
                    }}
                  >
                    {criterion.title}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '11px',
                      color: 'var(--text-ter)',
                      background: 'var(--rule)',
                      borderRadius: '4px',
                      padding: '2px 6px',
                    }}
                  >
                    {pct}%
                  </span>
                </div>

                <ScoreRow
                  criteriaId={criterion.id}
                  itemId={currentOption.id}
                  currentScore={scored}
                  onScore={handleScore}
                />

                <NoteField
                  value={notes[key] ?? ''}
                  onChange={(v) => handleNoteChange(currentOption.id, criterion.id, v)}
                />
              </div>
            );
          })}
        </div>

        {/* ── Fixed bottom navigation ─────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid var(--rule)',
            marginTop: '8px',
          }}
        >
          <button
            type="button"
            onClick={goPrev}
            disabled={currentIndex === 0}
            style={{
              background: 'transparent',
              border: '1px solid var(--rule-bright)',
              borderRadius: '8px',
              color: currentIndex === 0 ? 'var(--text-ter)' : 'var(--text-sec)',
              fontFamily: 'var(--sans)',
              fontSize: '14px',
              padding: '12px 20px',
              cursor: currentIndex === 0 ? 'default' : 'pointer',
              opacity: currentIndex === 0 ? 0.4 : 1,
            }}
          >
            ← Back
          </button>

          {isLastOption ? (
            <button
              type="button"
              onClick={goNext}
              style={{
                background: currentOptionAllScored ? 'var(--green)' : 'var(--mustard)',
                border: 'none',
                borderRadius: '8px',
                color: '#0B0F19',
                fontFamily: 'var(--sans)',
                fontSize: '14px',
                fontWeight: 700,
                padding: '12px 24px',
                cursor: 'pointer',
              }}
            >
              Submit ✓
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              style={{
                background: 'var(--mustard)',
                border: 'none',
                borderRadius: '8px',
                color: '#0B0F19',
                fontFamily: 'var(--sans)',
                fontSize: '14px',
                fontWeight: 700,
                padding: '12px 24px',
                cursor: 'pointer',
              }}
            >
              Next →
            </button>
          )}
        </div>

        {withdrawControl && (
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
            {withdrawControl}
          </div>
        )}

        {/* ── Host floating controls ──────────────────────────────────────────── */}
        {isHost && (
          <div
            style={{
              marginTop: '24px',
              padding: '14px 16px',
              background: 'var(--bg-raised)',
              border: '1px solid var(--rule-bright)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '12px',
                color: 'var(--text-ter)',
                flex: 1,
              }}
            >
              {membersComplete} of {presenceMembers.length} members finished
            </span>
            <button
              type="button"
              onClick={handleReveal}
              disabled={allScores.length === 0 || isLoading}
              style={{
                background: revealConfirm ? 'var(--coral)' : 'var(--mustard)',
                border: 'none',
                borderRadius: '8px',
                color: '#0B0F19',
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                fontWeight: 700,
                padding: '10px 18px',
                cursor: allScores.length === 0 ? 'not-allowed' : 'pointer',
                opacity: allScores.length === 0 ? 0.4 : 1,
              }}
            >
              {revealConfirm ? REVEAL_CONFIRM_LABEL : REVEAL_RESULTS_LABEL}
            </button>
          </div>
        )}
        {isHost && revealError && (
          <div style={{ marginTop: '8px' }}>
            <RevealErrorLine message={revealError} />
          </div>
        )}
      </div>
    </>
  );
}
